/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, DestroyRef, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {ModalService, ToastService} from '@siemens/ix-angular';
import {TranslateService} from '@ngx-translate/core';
import {combineLatest, firstValueFrom, switchMap} from 'rxjs';

import {
  AccessRequest,
  AccessRequestStatus,
} from '../../../../models/AccessRequest';
import {AccessRequestManagementService} from '../../../../services/access-request-management.service';
import {GeneralDataHandlingService} from '../../../../services/general.data-handling.service';
import {UserService} from '../../../../services/user.service';
import {
  DeclineAccessRequestResult,
  ModalDeclineAccessRequestComponent,
} from '../modal-decline-access-request/modal-decline-access-request.component';
import {ModalGrantAccessRequestComponent} from '../modal-grant-access-request/modal-grant-access-request.component';

/** Key-value pair used to render the access-request detail list. */
export interface AccessRequestField {
  labelKey: string;
  value: string;
}

/** A role entry ready for display in the disabled roles select. */
export interface AccessRequestRoleItem {
  /** Translated display label. */
  label: string;
  /** Raw role value (used as select-item value). */
  value: string;
}

@Component({
  selector: 'app-edit-access-request',
  templateUrl: './edit-access-request.component.html',
  styleUrl: './edit-access-request.component.scss',
  standalone: false,
})
export class EditAccessRequestComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly accessRequestManagementService = inject(
    AccessRequestManagementService,
  );
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  loading = false;
  submitting = false;
  accessRequest: AccessRequest | null = null;
  fields: AccessRequestField[] = [];
  roleItems: AccessRequestRoleItem[] = [];

  /** True when the loaded request has already been accepted or rejected. */
  get isAlreadyHandled(): boolean {
    return (
      this.accessRequest?.status === AccessRequestStatus.ACCEPTED ||
      this.accessRequest?.status === AccessRequestStatus.REJECTED
    );
  }

  private organizationId = '';

  readonly backRoute = 'app/list-access-requests';

  ngOnInit(): void {
    combineLatest([this.userService.userDataChange, this.route.params])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(([user, params]) => {
          this.organizationId = user?.organization.id ?? '';
          this.loading = true;
          const requestId = params['id'] as string;
          return this.accessRequestManagementService.getAccessRequest$(
            this.organizationId,
            requestId,
          );
        }),
      )
      .subscribe({
        next: (response) => {
          this.accessRequest = response.data;
          this.buildFields(response.data);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.accessRequest) {
          this.buildFields(this.accessRequest);
        }
      });
  }

  async onBackButtonClick(): Promise<void> {
    await this.router.navigate([this.backRoute]);
  }

  async openGrantModal(): Promise<void> {
    const instance = await this.modalService.open({
      content: ModalGrantAccessRequestComponent,
      size: '480',
      centered: true,
      closeOnBackdropClick: false,
    });

    instance.onClose.on(async () => {
      await this.updateStatus(AccessRequestStatus.ACCEPTED);
    });
  }

  async openDeclineModal(): Promise<void> {
    const instance = await this.modalService.open({
      content: ModalDeclineAccessRequestComponent,
      size: '480',
      centered: true,
      closeOnBackdropClick: false,
    });

    instance.onClose.on(async (result: DeclineAccessRequestResult) => {
      await this.updateStatus(AccessRequestStatus.REJECTED, result.reason);
    });
  }

  private async updateStatus(
    status: AccessRequestStatus,
    reason?: string,
  ): Promise<void> {
    if (!this.accessRequest) {
      return;
    }
    this.submitting = true;
    try {
      await firstValueFrom(
        this.accessRequestManagementService.updateAccessRequestStatus$(
          this.organizationId,
          this.accessRequest.id,
          status,
          reason,
        ),
      );
      const successKey =
        status === AccessRequestStatus.ACCEPTED
          ? 'um.access_requests.grant_success'
          : 'um.access_requests.decline_success';
      await this.toastService.show({
        message: this.translateService.instant(successKey),
        type: 'success',
      });
      await this.router.navigate([this.backRoute]);
    } catch {
      const errorKey =
        status === AccessRequestStatus.ACCEPTED
          ? 'um.access_requests.grant_error'
          : 'um.access_requests.decline_error';
      await this.toastService.show({
        message: this.translateService.instant(errorKey),
        type: 'error',
      });
    } finally {
      this.submitting = false;
    }
  }

  private buildFields(request: AccessRequest): void {
    const form = 'battery_pass.public.request_access_form';
    this.fields = [
      {labelKey: `${form}.label_first_name`, value: request.firstName},
      {labelKey: `${form}.label_last_name`, value: request.lastName},
      {labelKey: `${form}.label_email`, value: request.email},
      {labelKey: `${form}.label_organization`, value: request.organization},
      {
        labelKey: 'um.access_requests.created_at',
        value: request.createdAt
          ? new Date(request.createdAt).toLocaleDateString()
          : '',
      },
      {
        labelKey: `${form}.label_purpose_of_access`,
        value: request.purposeOfAccess,
      },
    ];

    this.roleItems = Array.isArray(request.roles)
      ? request.roles.map((role) => ({
          label: this.translateService.instant(
            'um.roles.' + GeneralDataHandlingService.returnRoleLabel(role),
          ),
          value: role,
        }))
      : [];
  }
}
